import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { trackReportFeature } from "@/analytics/report-analytics";
import APIClient from "@/APIClient";
import { QueryKey } from "./query-keys";
import type { Ref } from "vue";
import type {
  CreateFindingPayload,
  Finding,
  UpdateFindingPayload,
} from "@/types/findings.types";
import { useAIGuardError } from "@/composable/useAIGuardError";

export const useCreateFinding = () => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async (payload: CreateFindingPayload) => {
      return await APIClient.createFinding(payload);
    },
    onSuccess: (finding: Finding) => {
      trackReportFeature("finding_created", { via: "manual" });
      if (finding.sectionId) {
        queryClient.invalidateQueries({
          queryKey: [QueryKey.FINDINGS, { section: finding.sectionId }],
        });
      } else {
        // Invalidate unsorted findings query
        queryClient.invalidateQueries({
          queryKey: [QueryKey.FINDINGS, { unsorted: finding.reportId }],
        });
      }
    },
    onError: createErrorHandler("toast:fieldObservations.partCreateError"),
  });
};
export const useUpdateFinding = (id: Ref<number>) => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async (payload: UpdateFindingPayload) => {
      return await APIClient.updateFinding(id.value, payload);
    },
    onSuccess: (finding: Finding) => {
      trackReportFeature("finding_edited");
      if (finding.sectionId) {
        queryClient.invalidateQueries({
          queryKey: [QueryKey.FINDINGS, { section: finding.sectionId }],
        });
      } else if (finding.reportId) {
        // Invalidate unsorted findings query
        queryClient.invalidateQueries({
          queryKey: [QueryKey.FINDINGS, { unsorted: finding.reportId }],
        });
      }
      // Also invalidate diagnostic images to refresh findings shown in photos list
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === QueryKey.DIAGNOSTIC_IMAGES,
      });
    },
    onError: createErrorHandler(),
  });
};

export const useUpdateFindingById = () => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: UpdateFindingPayload;
    }) => {
      return await APIClient.updateFinding(id, payload);
    },
    onSuccess: (finding: Finding) => {
      trackReportFeature("finding_edited");
      if (finding.sectionId) {
        queryClient.invalidateQueries({
          queryKey: [QueryKey.FINDINGS, { section: finding.sectionId }],
        });
      } else if (finding.reportId) {
        // Invalidate unsorted findings query
        queryClient.invalidateQueries({
          queryKey: [QueryKey.FINDINGS, { unsorted: finding.reportId }],
        });
      }
      // Also invalidate diagnostic images to refresh findings shown in photos list
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === QueryKey.DIAGNOSTIC_IMAGES,
      });
    },
    onError: createErrorHandler(),
  });
};

export const useMoveFinding = () => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async ({
      newSectionId,
      id,
    }: {
      oldSectionId: number | null;
      newSectionId: number | null;
      reportId: number;
      id: number;
    }) => {
      return await APIClient.moveFinding(id, newSectionId);
    },
    onSuccess: (_, data) => {
      trackReportFeature("finding_moved");
      // Invalidate new location
      if (data.newSectionId) {
        queryClient.invalidateQueries({
          queryKey: [QueryKey.FINDINGS, { section: data.newSectionId }],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: [QueryKey.FINDINGS, { unsorted: data.reportId }],
        });
      }
      // Invalidate old location
      if (data.oldSectionId) {
        queryClient.invalidateQueries({
          queryKey: [QueryKey.FINDINGS, { section: data.oldSectionId }],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: [QueryKey.FINDINGS, { unsorted: data.reportId }],
        });
      }
    },
    onError: createErrorHandler(),
  });
};

export const useDeleteFinding = () => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async (id: number) => {
      return await APIClient.deleteFinding(id);
    },
    onSuccess: () => {
      trackReportFeature("finding_deleted");
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FINDINGS],
        predicate: (query) => query.queryKey[0] === QueryKey.FINDINGS,
      });
    },
    onError: createErrorHandler("toast:findings.delete-error"),
  });
};

// NOTE: This mutation is deprecated. Use useAddManyReportImagesToFinding from report-images.query.ts instead.
// Kept for backwards compatibility but now forwards to the ReportImage API.
export const useAddManyImagesToFinding = () => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async ({
      findingId,
      imageIds,
    }: {
      findingId: number;
      imageIds: number[];
      diagnosticId: number;
    }) => {
      // Now uses reportImageIds
      return await APIClient.addManyReportImagesToFinding(findingId, imageIds);
    },
    onSuccess: (_, { diagnosticId }) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FINDINGS],
        predicate: (query) => query.queryKey[0] === QueryKey.FINDINGS,
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORT_IMAGES],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.DIAGNOSTIC_IMAGES, { diagnosticId }],
      });
    },
    onError: createErrorHandler("toast:findings.add-images-error"),
  });
};

/**
 * Récupère les findings non triés (sans section) d'un rapport
 */
export const useGetUnsortedFindings = (reportId: Ref<number>) => {
  return useQuery({
    queryKey: [QueryKey.FINDINGS, { unsorted: reportId }],
    queryFn: async () => {
      return await APIClient.fetchUnsortedFindings(reportId.value);
    },
  });
};
